import { format } from 'date-fns'
import { AlertCircle, AlertTriangle, Check, CheckCircle2, Copy, Info, Loader2, MessageSquare } from 'lucide-react'
import { type ComponentType, useEffect, useState } from 'react'

import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { confirmDialog } from '@/lib/confirm-dialog'
import { type ToastHistoryItem, type ToastHistoryType, useToastHistoryStore } from '@/store/useToastHistoryStore'

const TYPE_STYLES: Record<ToastHistoryType, { Icon: ComponentType<{ className?: string }>; className: string }> = {
  success: { Icon: CheckCircle2, className: 'text-emerald-400' },
  error: { Icon: AlertCircle, className: 'text-red-400' },
  warning: { Icon: AlertTriangle, className: 'text-amber-400' },
  info: { Icon: Info, className: 'text-sky-400' },
  loading: { Icon: Loader2, className: 'text-muted-foreground' },
  default: { Icon: MessageSquare, className: 'text-muted-foreground' },
}

const COPIED_RESET_MS = 1500

type ToastHistoryRowProps = {
  item: ToastHistoryItem
  copied: boolean
  onCopy: (item: ToastHistoryItem) => void
}

const ToastHistoryRow = ({ item, copied, onCopy }: ToastHistoryRowProps) => {
  const { Icon, className } = TYPE_STYLES[item.type]

  return (
    <li className="flex items-start gap-3 rounded-lg border border-border px-3 py-2.5">
      <Icon className={`mt-0.5 size-4 shrink-0 ${className}`} />
      <div className="min-w-0 flex-1">
        {item.title && <p className="text-sm wrap-break-word whitespace-pre-wrap">{item.title}</p>}
        {item.description && (
          <p className="mt-0.5 text-xs text-muted-foreground wrap-break-word whitespace-pre-wrap">{item.description}</p>
        )}
        <p className="mt-1 text-[11px] text-muted-foreground/70">{format(item.updatedAt, 'M/d HH:mm:ss')}</p>
      </div>
      <button
        type="button"
        onClick={() => onCopy(item)}
        className="shrink-0 p-1 text-muted-foreground hover:text-foreground"
        aria-label="コピー"
      >
        {copied ? <Check className="size-4 text-emerald-400" /> : <Copy className="size-4" />}
      </button>
    </li>
  )
}

type ToastHistorySheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const ToastHistorySheet = ({ open, onOpenChange }: ToastHistorySheetProps) => {
  const items = useToastHistoryStore((state) => state.items)
  const clear = useToastHistoryStore((state) => state.clear)
  const markSeen = useToastHistoryStore((state) => state.markSeen)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  useEffect(() => {
    if (open) markSeen()
  }, [open, markSeen])

  useEffect(() => {
    if (!copiedId) return
    const timer = setTimeout(() => setCopiedId(null), COPIED_RESET_MS)
    return () => clearTimeout(timer)
  }, [copiedId])

  // コピー完了をトーストで知らせると履歴に積まれるため、アイコンの切り替えで示す
  const handleCopy = async (item: ToastHistoryItem) => {
    const text = [item.title, item.description].filter(Boolean).join('\n')
    await navigator.clipboard.writeText(text)
    setCopiedId(item.id)
  }

  const handleClear = async () => {
    const ok = await confirmDialog({ title: 'トースト履歴をすべて削除しますか？', destructive: true })
    if (ok) clear()
  }

  return (
    <BottomSheet title="トースト履歴" open={open} onOpenChange={onOpenChange} snapPoints={[0.92]}>
      <div className="space-y-3 px-4 pb-6">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">直近 {items.length} 件</p>
          <Button variant="ghost" size="sm" onClick={handleClear} disabled={items.length === 0}>
            すべて削除
          </Button>
        </div>
        {items.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">履歴はありません</p>
        ) : (
          <ul className="space-y-2">
            {items.map((item) => (
              <ToastHistoryRow key={item.id} item={item} copied={copiedId === item.id} onCopy={handleCopy} />
            ))}
          </ul>
        )}
      </div>
    </BottomSheet>
  )
}
